<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header
                :title="titlePrefix + ' ' + $tc('resources.mail.name', 1)"
            />
            <v-card-text class="pb-0">
                <v-col cols="12">
                    <v-text-field
                        v-model="mail.subject"
                        :label="$t('resources.mail.attributes.subject')"
                        :hint="$t('resources.mail.hints.subject')"
                        :counter="255"
                        :error-messages="subjectErrors"
                        @input="$v.mail.subject.$touch()"
                        @blur="$v.mail.subject.$touch()"
                    ></v-text-field>
                </v-col>
                <v-col>
                    <v-combobox
                        v-model="mail.recipients"
                        :label="$t('resources.mail.hints.recipients')"
                        :items="[]"
                        multiple
                        chips
                        :delimiters="[',', ';', ' ']"
                        deletable-chips
                        clearable
                        :error-messages="recipientsErrors"
                        @input="$v.mail.recipients.$touch()"
                        @blur="$v.mail.recipients.$touch()"
                    ></v-combobox>
                </v-col>
                <v-col>
                    <v-combobox
                        v-model="mail.bcc"
                        :label="$t('resources.mail.attributes.bcc')"
                        :items="[]"
                        multiple
                        chips
                        :delimiters="[',', ';', ' ']"
                        deletable-chips
                        clearable
                        :error-messages="bccErrors"
                        @input="$v.mail.bcc.$touch()"
                        @blur="$v.mail.bcc.$touch()"
                    ></v-combobox>
                </v-col>

                <v-col v-if="mail.can_send_to_resellers">
                    <v-switch
                        v-model="mail.send_to_resellers"
                        :label="$t('resources.mail.hints.send-to-resellers')"
                    ></v-switch>
                </v-col>
                <v-row>
                    <v-col>
                        <v-label>{{
                            $t('resources.mail.hints.content')
                        }}</v-label>
                        <tiptap-vuetify
                            v-model="mail.content"
                            :toolbar-attributes="{ color: 'grey lighten-3' }"
                            :extensions="extensions"
                            @init="onEditorCreated"
                            id="test"
                        ></tiptap-vuetify>
                        <div class="v-text-field__details">
                            <div class="v-messages theme--light">
                                <div class="v-messages__wrapper">
                                    {{ $t('resources.mail.hints.content') }}
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>
                <v-row class="mb-2">
                    <v-col class="grey lighten-4">
                        <label class="subtitle-2">
                            <v-icon left>mdi-account-cog</v-icon>Variáveis
                        </label>

                        <v-chip-group>
                            <span
                                v-for="(variable,
                                index) in mail.parsedVariables"
                                :key="'var' + index"
                            >
                                <v-chip
                                    @click="addVariable(variable.name)"
                                    class="ma-2"
                                    v-if="!variable.hint && !variable.example"
                                >
                                    {{ variable.name }}
                                </v-chip>

                                <v-tooltip bottom v-else>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-chip
                                            @click="addVariable(variable.name)"
                                            class="ma-2"
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            {{ variable.name }}
                                        </v-chip>
                                    </template>

                                    <span v-if="variable.hint">
                                        {{ variable.hint }}
                                    </span>
                                    <br
                                        v-if="variable.hint && variable.example"
                                    />

                                    <span v-if="variable.example">
                                        Exemplo:
                                        <span v-html="variable.example"></span>
                                    </span>
                                </v-tooltip>
                            </span>
                        </v-chip-group>
                    </v-col>
                </v-row>
            </v-card-text>

            <form-actions :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import BaseModel from '@/models/BaseModel'
import { validationMixin } from 'vuelidate'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'

import {
    TiptapVuetify,
    Heading,
    Bold,
    Italic,
    Strike,
    Underline,
    Code,
    Paragraph,
    BulletList,
    OrderedList,
    ListItem,
    Link,
    Blockquote,
    HardBreak,
    HorizontalRule,
    History,
} from 'tiptap-vuetify'

export default {
    components: {
        CardHeader,
        FormActions,
        TiptapVuetify,
    },

    mixins: [validationMixin],

    props: {
        titlePrefix: {
            type: String,
            default: 'Editar',
        },
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
    },

    data() {
        return {
            loading: false,
            extensions: [
                History,
                Blockquote,
                Link,
                Underline,
                Strike,
                Italic,
                ListItem,
                BulletList,
                OrderedList,
                [
                    Heading,
                    {
                        options: {
                            levels: [1, 2, 3],
                        },
                    },
                ],
                Bold,
                Code,
                HorizontalRule,
                Paragraph,
                HardBreak,
            ],
            editor: null,
        }
    },

    validations() {
        return {
            mail: {
                subject: { required, maxLength: maxLength(255) },
                recipients: {
                    maxLength: maxLength(255),
                    requiredIf: requiredIf(() => {
                        return !this.mail.bcc.length
                    }),
                },
                bcc: {
                    maxLength: maxLength(255),
                },
                content: { required },
            },
        }
    },

    computed: {
        mail() {
            return this.model
        },

        subjectErrors() {
            const errors = []
            if (!this.$v.mail.subject.$dirty) return errors

            !this.$v.mail.subject.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t('resources.mail.hints.subject'),
                    }),
                )
            !this.$v.mail.subject.maxLength &&
                errors.push(
                    this.$t('forms.errors.maxLength', {
                        label: this.$t('resources.mail.hints.subject'),
                        length: 255,
                    }),
                )

            return errors
        },

        recipientsErrors() {
            const errors = []
            if (!this.$v.mail.recipients.$dirty) return errors

            !this.$v.mail.recipients.requiredIf &&
                errors.push(
                    this.$t('forms.errors.requiredIf', {
                        label: this.$t('resources.mail.hints.recipients'),
                    }),
                )
            !this.$v.mail.recipients.maxLength &&
                errors.push(
                    this.$t('forms.errors.maxLength', {
                        label: this.$t('resources.mail.hints.recipients'),
                        length: 255,
                    }),
                )

            return errors
        },

        bccErrors() {
            const errors = []
            if (!this.$v.mail.bcc.$dirty) return errors

            !this.$v.mail.recipients.maxLength &&
                errors.push(
                    this.$t('forms.errors.maxLength', {
                        label: this.$t('resources.mail.attributes.bcc'),
                        length: 255,
                    }),
                )

            return errors
        },

        contentErrors() {
            const errors = []
            if (!this.$v.mail.content.$dirty) return errors

            !this.$v.mail.content.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t('resources.mail.hints.content'),
                    }),
                )

            return errors
        },
    },

    methods: {
        addVariable(variable) {
            const transaction = this.editor.state.tr.insertText(variable)
            this.editor.view.dispatch(transaction)
        },

        onEditorCreated({ editor }) {
            this.editor = editor
        },

        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.mail.clone()

            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('forms.messages.saved', {
                    resource: this.$tc('resources.mail.name', 1),
                    name: clone.subject,
                }),
            )
        },

        clear() {
            this.$refs.form.reset()
        },
    },
}
</script>

<style></style>
